import { WatchQueryFetchPolicy } from '@apollo/client';
import { useEffect } from 'react';

import {
  PublicTeam,
  Team,
  TeamOrPublicTeam,
  useGetTeamDetailQuery,
} from '@graphql/generated';
import { SESSION_SELECTED_TEAM_NAME } from '@src/constants/constants';
import { isApolloResourceNotFound } from '@utils/isResourceNotFoundError';

/**
 * Retrieves the team shown on the Team Details screen through `getPublicTeam`.
 *
 * `getTeam` fails with "The requested team could not be found" for a user who
 * opened the app from a team invite link, because that user is not a member of
 * the team yet. `getPublicTeam` resolves in both cases: it returns the full
 * `Team` branch for members and the limited `PublicTeam` branch otherwise.
 *
 * `includePending` mirrors `getTeam`'s argument of the same name and is passed
 * through unchanged. Note that it is part of the query's variables, so two call
 * sites reading the same team with different values address two separate cache
 * entries — any `refetchQueries` entry must repeat the value its target used.
 */
export const useTeamDetailFromQuery = ({
  teamUUId,
  fetchPolicy = 'cache-and-network',
  includePending,
  withMembers = true,
  shouldClearSelectedTeamOnNotFound = false,
}: {
  teamUUId?: Team['uuid'] | PublicTeam['uuid'];
  fetchPolicy?: WatchQueryFetchPolicy;
  includePending?: boolean;
  /**
   * Select TeamMembersDetail (users / invitations / invoices) on the Team
   * branch. Pass false from readers that never render those lists
   * (TA-10847). Like `includePending`, it is a query variable: readers that
   * should share one request must agree on it.
   */
  withMembers?: boolean;
  /**
   * Clear the persisted selected-team name when this team turns out not to
   * exist. Opt-in, and off by default: TeamInvitationCard and DrawerHeader
   * read teams they do not own the selection for, and a stale uuid in an old
   * chat invitation must not blank out the drawer's current team.
   *
   * Pass the route's focus state rather than a bare `true` — the web stacks
   * keep inactive screens mounted, so a late answer on a screen the user has
   * navigated away from would otherwise clear the selection that now belongs
   * to another route.
   */
  shouldClearSelectedTeamOnNotFound?: boolean;
}) => {
  const {
    data: { getPublicTeam } = {},
    loading,
    error,
    refetch,
  } = useGetTeamDetailQuery({
    variables: { id: teamUUId || '', includePending, withMembers },
    fetchPolicy,
    skip: !teamUUId,
  });

  const team =
    getPublicTeam?.__typename === 'Team' ||
    getPublicTeam?.__typename === 'PublicTeam'
      ? (getPublicTeam as TeamOrPublicTeam)
      : undefined;

  // Same predicate the errorLink uses to keep a not-found 401 off the logout
  // path, so the link and this screen cannot disagree about what not-found
  // means: RESOURCE_NOT_FOUND first, the legacy wording as the fallback. It
  // has to look past `error.message` — the backend answers a missing team
  // with HTTP 401, and for a non-2xx Apollo leaves `graphQLErrors` empty and
  // puts the body under `networkError.result`, leaving the top-level message
  // as only "Response not successful: Received status code 401" (TA-10850 QA).
  const isNotFound = isApolloResourceNotFound(error);

  // The not-found cleanup useTeamFromQuery and usePublicTeamFromQuery both
  // carry — without it a team that is gone keeps its name in the web drawer,
  // and in sessionStorage, so a reload restores it for the whole session.
  //
  // No cache eviction here: Team has no keyFields policy, so it is normalized
  // by `id`, and `cache.identify({ __typename: 'Team', uuid })` returns
  // undefined — the eviction those two hooks do is a no-op. The failed read
  // wrote nothing to evict anyway.
  useEffect(() => {
    if (!isNotFound || !shouldClearSelectedTeamOnNotFound) return;

    // The `sessionStorage` getter itself throws in Safari private mode and
    // wherever storage is blocked, so this is guarded the same way
    // pendingTeamJoinRequests does it — storage availability must not break
    // the unavailable screen.
    try {
      if (typeof window === 'undefined') return;
      window.sessionStorage?.removeItem(SESSION_SELECTED_TEAM_NAME);
    } catch {
      // Nothing to clean up if the key was never readable in the first place.
    }
  }, [isNotFound, shouldClearSelectedTeamOnNotFound]);

  return {
    team,
    /** The team does not exist for this viewer — a stale uuid, or deleted. */
    isNotFound,
    // Only a member gets the full Team branch; non-members get PublicTeam.
    fullTeam: team?.__typename === 'Team' ? (team as Team) : undefined,
    publicTeam:
      team?.__typename === 'PublicTeam' ? (team as PublicTeam) : undefined,
    hasPendingMemberAdditionRequest:
      team?.hasPendingMemberAdditionRequest ?? false,
    loading,
    error,
    refetch,
  };
};
